<template>
    <div class="tw-bg-white tw-py-4 tw-rounded tw-border tw-border-solid tw-border-gray-200 tw-h-[220px]">
        <div class="tw-flex tw-items-center tw-mb-6 tw-gap-3 tw-px-4">
            <div class="tw-flex tw-items-center tw-gap-2">
                <p class="tw-p-2x tw-font-bold tw-text-lg">{{ $t('common.treated_followups') }}</p>
                <p class="tw-px-1 tw-bg-blue-600 tw-text-white tw-text-sm tw-rounded">{{ new
                    Intl.NumberFormat().format(total) }}</p>
            </div>
        </div>
        <apexchart type="area" height="140" :options="options" :series="series"></apexchart>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { followup } from '@/api/dashboard';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const loading = ref(true);
const data = ref([]);

// Calculations
const total = computed(() => data.value.reduce((acc, curr) => acc + curr.count, 0));

// Chart configuration
const options = computed(() => ({
    chart: {
        type: 'area',
        toolbar: { show: false },
        zoom: { enabled: false },
        foreColor: '#64748b'
    },
    colors: ['#2563eb'],
    dataLabels: {
        enabled: true,
        style: {
            fontSize: '10px',
            fontFamily: 'Inter, sans-serif',
            colors: ['#fff']
        },
        background: {
            enabled: true,
            foreColor: '#2563eb',
            padding: 4,
            borderRadius: 8,
            borderWidth: 0,
            opacity: 0.9,
        }
    },
    stroke: {
        width: 2,
        curve: 'smooth'
    },
    fill: {
        type: 'gradient',
        gradient: {
            shadeIntensity: 0.3,
            opacityFrom: 0.6,
            opacityTo: 0.1,
            stops: [0, 90, 100],
            colorStops: [
                [
                    {
                        offset: 0,
                        color: '#2563eb',
                        opacity: 0.6
                    },
                    {
                        offset: 100,
                        color: '#2563eb',
                        opacity: 0.1
                    }
                ]
            ]
        }
    },
    xaxis: {
        type: 'datetime',
        labels: {
            style: {
                fontSize: '11px',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 500,
            },
            format: 'MMM d',
            datetimeUTC: false
        },
        axisTicks: { show: false }
    },
    yaxis: { show: false },
    grid: {
        borderColor: '#f1f5f9',
        strokeDashArray: 4,
        padding: { top: 0, right: 20, bottom: 0, left: 20 }
    },
    tooltip: {
        theme: 'light',
        style: {
            fontSize: '12px',
            fontFamily: 'Inter, sans-serif'
        },
        x: {
            format: 'MMM d, yyyy'
        },
        marker: {
            fillColors: ['#2563eb']
        }
    }
}));

// Correct data format for datetime
const series = computed(() => [{
    name: t('common.treated_followups'),
    data: data.value.map(i => ({
        x: new Date(i.date).getTime(),
        y: i.count
    }))
}]);

const getData = async () => {
    loading.value = true;

    try {
        const res = await followup.dailyTreatedFollowups();
        if (res.data.code == 'SUCCESS') {
            data.value = res.data.data;
        }
    } catch (err) {
        console.error(err);
    } finally {
        loading.value = false;
    }
}

getData();
</script>

<style scoped>
/* Blue accent styling */
.tw-bg-blue-600 {
    background-color: #2563eb;
}
</style> 